import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Cpu, Zap, Sparkles, Loader2 } from 'lucide-react';

// ============================================
// QUANTUM PAGE LOADER
// ============================================
export const QuantumPageLoader: React.FC<{ message?: string }> = ({ 
  message = "Initializing Quantum Interface..." 
}) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 flex items-center justify-center">
    <div className="relative">
      <motion.div 
        className="absolute inset-0 rounded-full border-4 border-blue-500/30 border-t-blue-500"
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        style={{ width: '120px', height: '120px' }}
      />
      <motion.div 
        className="absolute inset-2 rounded-full border-4 border-purple-500/30 border-t-purple-500"
        animate={{ rotate: -360 }}
        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        style={{ width: '104px', height: '104px' }}
      />
      <motion.div 
        className="absolute inset-4 rounded-full border-4 border-pink-500/30 border-t-pink-500"
        animate={{ rotate: 180 }}
        transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        style={{ width: '88px', height: '88px' }}
      />
      <div className="relative w-[120px] h-[120px] rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-2xl">
        <Brain className="w-12 h-12 text-white animate-pulse" />
      </div>
      <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 w-64 text-center">
        <p className="text-blue-400 text-sm font-bold uppercase tracking-[0.3em] animate-pulse">
          QUANTUM LOADING
        </p>
        <p className="text-white/60 text-xs font-mono mt-2">{message}</p>
      </div>
    </div>
  </div>
);

// ============================================
// QUANTUM COMPONENT LOADER
// ============================================
export const QuantumComponentLoader: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ 
  size = 'md' 
}) => {
  const dimensions = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32'
  };

  const iconSizes = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10'
  };

  return (
    <div className="flex items-center justify-center p-8">
      <div className="relative">
        <motion.div 
          className={`absolute inset-0 rounded-full border-4 border-blue-500/30 border-t-blue-500 ${dimensions[size]}`}
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        />
        <motion.div 
          className={`absolute inset-1 rounded-full border-4 border-purple-500/30 border-t-purple-500 ${dimensions[size]}`}
          animate={{ rotate: -360 }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          style={{ width: 'calc(100% - 8px)', height: 'calc(100% - 8px)' }}
        />
        <div className={`relative ${dimensions[size]} rounded-full bg-gradient-to-br from-blue-500/20 to-purple-600/20 backdrop-blur-sm flex items-center justify-center`}>
          <Zap className={`${iconSizes[size]} text-blue-400 animate-pulse`} />
        </div>
      </div>
    </div>
  );
};

// ============================================
// QUANTUM SUSPENSE WRAPPER
// ============================================
import { Suspense } from 'react';

export const QuantumSuspense: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ 
  children, 
  fallback = <QuantumComponentLoader size="lg" /> 
}) => (
  <Suspense fallback={fallback}>
    {children}
  </Suspense>
);